import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CloudArrowUpIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  LockClosedIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const DocumentUpload = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [dragActive, setDragActive] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    tags: '',
    permissions: 'team', // 'public', 'team', 'private'
    department: '',
    version: '1.0'
  });

  const categories = [
    'HR Policies',
    'Medical Procedures',
    'Safety Protocols',
    'Training Materials',
    'Forms & Templates',
    'IT Security',
    'Quality Assurance',
    'Financial Records',
    'Research',
    'Other'
  ];

  const departments = [
    'Administration',
    'Cardiology',
    'Emergency Medicine',
    'Human Resources',
    'Information Technology',
    'Laboratory',
    'Nursing',
    'Pharmacy',
    'Radiology',
    'Surgery'
  ];

  const maxFileSize = 50 * 1024 * 1024; // 50MB
  const allowedTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/plain',
    'image/jpeg',
    'image/png',
    'image/gif'
  ];

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (fileList) => {
    const newFiles = Array.from(fileList).map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      size: file.size,
      type: file.type,
      status: 'ready', // 'ready', 'uploading', 'completed', 'error'
      error: null
    }));

    // Validate files
    const validatedFiles = newFiles.map(fileObj => {
      const errors = [];
      
      if (!allowedTypes.includes(fileObj.type)) {
        errors.push('File type not supported');
      }
      
      if (fileObj.size > maxFileSize) {
        errors.push('File size exceeds 50MB limit');
      }
      
      if (errors.length > 0) {
        return { ...fileObj, status: 'error', error: errors.join(', ') };
      }
      
      return fileObj;
    });

    setFiles(prev => [...prev, ...validatedFiles]);
    
    // Auto-fill title if it's empty and we have one file
    if (!formData.title && validatedFiles.length === 1) {
      const fileName = validatedFiles[0].name.replace(/\.[^/.]+$/, "");
      setFormData(prev => ({ ...prev, title: fileName }));
    }
  };

  const removeFile = (fileId) => {
    setFiles(files.filter(f => f.id !== fileId));
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[fileId];
      return newProgress;
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (type) => {
    if (type.includes('pdf')) return '📄';
    if (type.includes('word') || type.includes('document')) return '📝';
    if (type.includes('excel') || type.includes('sheet')) return '📊';
    if (type.includes('powerpoint') || type.includes('presentation')) return '📋';
    if (type.includes('image')) return '🖼️';
    if (type.includes('text')) return '📄';
    return '📎';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const simulateUpload = (fileId) => {
    return new Promise((resolve) => {
      let progress = 0;
      const interval = setInterval(() => {
        progress += Math.random() * 20;
        if (progress >= 100) {
          progress = 100;
          clearInterval(interval);
          setUploadProgress(prev => ({ ...prev, [fileId]: 100 }));
          resolve();
        } else {
          setUploadProgress(prev => ({ ...prev, [fileId]: Math.floor(progress) }));
        }
      }, 200);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.title.trim()) {
      alert('Please enter a title for the document(s)');
      return;
    }
    
    if (!formData.category) {
      alert('Please select a category');
      return;
    }

    if (files.length === 0) {
      alert('Please select at least one file to upload');
      return;
    }

    const validFiles = files.filter(f => f.status !== 'error');
    if (validFiles.length === 0) {
      alert('Please fix file errors before uploading');
      return;
    }

    setUploading(true);

    try {
      // Update file status to uploading
      setFiles(prev => prev.map(f => 
        f.status !== 'error' ? { ...f, status: 'uploading' } : f
      ));

      // Simulate upload for each valid file
      await Promise.all(
        validFiles.map(async (fileObj) => {
          await simulateUpload(fileObj.id);
          setFiles(prev => prev.map(f => 
            f.id === fileObj.id ? { ...f, status: 'completed' } : f
          ));
        })
      );

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      alert('Documents uploaded successfully!');
      navigate('/documents/my-documents');
    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload failed. Please try again.');
      setFiles(prev => prev.map(f => 
        f.status === 'uploading' ? { ...f, status: 'error', error: 'Upload failed' } : f
      ));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      {/* Custom Header for Document Upload */}
      <div className="bg-white shadow">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center">
              <CloudArrowUpIcon className="h-8 w-8 text-blue-600 mr-3" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Upload Documents</h1>
                <p className="mt-2 text-sm text-gray-700">
                  Upload and organize your documents for easy access and sharing
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* File Upload Area */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Select Files</h2>
            
            <div
              className={`relative border-2 border-dashed rounded-lg p-6 ${
                dragActive 
                  ? 'border-blue-400 bg-blue-50' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <div className="text-center">
                <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                <div className="mt-4">
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <span className="mt-2 block text-sm font-medium text-gray-900">
                      Drop files here or click to upload
                    </span>
                    <input
                      ref={fileInputRef}
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      multiple
                      className="sr-only"
                      onChange={handleChange}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif"
                    />
                  </label>
                  <p className="mt-1 text-xs text-gray-500">
                    PDF, DOC, XLS, PPT, TXT, or image files up to 50MB each
                  </p>
                </div>
              </div>
            </div>

            {/* File List */}
            {files.length > 0 && (
              <div className="mt-6">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Selected Files</h3>
                <div className="space-y-3">
                  {files.map((fileObj) => (
                    <div key={fileObj.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center flex-1 min-w-0">
                        <span className="text-2xl mr-3">{getFileIcon(fileObj.type)}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {fileObj.name}
                          </p>
                          <div className="flex items-center mt-1 text-xs text-gray-500">
                            <span>{formatFileSize(fileObj.size)}</span>
                            {fileObj.status === 'uploading' && uploadProgress[fileObj.id] !== undefined && (
                              <>
                                <span className="mx-2">•</span>
                                <span>Uploading {uploadProgress[fileObj.id]}%</span>
                              </>
                            )}
                            {fileObj.status === 'completed' && (
                              <>
                                <span className="mx-2">•</span>
                                <span className="text-green-600">Completed</span>
                              </>
                            )}
                            {fileObj.status === 'error' && (
                              <>
                                <span className="mx-2">•</span>
                                <span className="text-red-600">{fileObj.error}</span>
                              </>
                            )}
                          </div>
                          {fileObj.status === 'uploading' && uploadProgress[fileObj.id] !== undefined && (
                            <div className="mt-2 w-full bg-gray-200 rounded-full h-1">
                              <div 
                                className="bg-blue-600 h-1 rounded-full transition-all duration-300"
                                style={{ width: `${uploadProgress[fileObj.id]}%` }}
                              ></div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center ml-4 space-x-2">
                        {fileObj.status === 'completed' && (
                          <CheckCircleIcon className="h-5 w-5 text-green-500" />
                        )}
                        {fileObj.status === 'error' && (
                          <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                        )}
                        {!uploading && (
                          <button
                            type="button"
                            onClick={() => removeFile(fileObj.id)}
                            className="p-1 rounded-full hover:bg-gray-200"
                          >
                            <XMarkIcon className="h-4 w-4 text-gray-400" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Document Information */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Document Information</h2>
            
            <div className="grid grid-cols-1 gap-6">
              {/* Title */}
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                  Title *
                </label>
                <input
                  type="text"
                  name="title"
                  id="title"
                  required
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter document title..."
                  value={formData.title}
                  onChange={handleInputChange}
                />
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  name="description"
                  id="description"
                  rows={3}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Describe the document content and purpose..."
                  value={formData.description}
                  onChange={handleInputChange}
                />
              </div>

              {/* Category and Department */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                    Category *
                  </label>
                  <select
                    name="category"
                    id="category"
                    required
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={formData.category}
                    onChange={handleInputChange}
                  >
                    <option value="">Select a category</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="department" className="block text-sm font-medium text-gray-700">
                    Department
                  </label>
                  <select
                    name="department"
                    id="department"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={formData.department}
                    onChange={handleInputChange}
                  >
                    <option value="">Select department</option>
                    {departments.map(dept => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Tags and Version */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="tags" className="block text-sm font-medium text-gray-700">
                    Tags
                  </label>
                  <input
                    type="text"
                    name="tags"
                    id="tags"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Enter tags separated by commas..."
                    value={formData.tags}
                    onChange={handleInputChange}
                  />
                  <p className="mt-1 text-xs text-gray-500">Separate multiple tags with commas</p>
                </div>

                <div>
                  <label htmlFor="version" className="block text-sm font-medium text-gray-700">
                    Version
                  </label>
                  <input
                    type="text"
                    name="version"
                    id="version"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="1.0"
                    value={formData.version}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Permissions */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Document Permissions</h2>
            
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  id="public"
                  name="permissions"
                  type="radio"
                  value="public"
                  checked={formData.permissions === 'public'}
                  onChange={handleInputChange}
                  className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300"
                />
                <label htmlFor="public" className="ml-3 flex items-center">
                  <GlobeAltIcon className="h-5 w-5 text-gray-400 mr-2" />
                  <div>
                    <div className="text-sm font-medium text-gray-700">Public</div>
                    <div className="text-xs text-gray-500">Anyone in the organization can view and download</div>
                  </div>
                </label>
              </div>

              <div className="flex items-center">
                <input
                  id="team"
                  name="permissions"
                  type="radio"
                  value="team"
                  checked={formData.permissions === 'team'}
                  onChange={handleInputChange}
                  className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300"
                />
                <label htmlFor="team" className="ml-3 flex items-center">
                  <UserGroupIcon className="h-5 w-5 text-gray-400 mr-2" />
                  <div>
                    <div className="text-sm font-medium text-gray-700">Team Only</div>
                    <div className="text-xs text-gray-500">Only your department team can access</div>
                  </div>
                </label>
              </div>

              <div className="flex items-center">
                <input
                  id="private"
                  name="permissions"
                  type="radio"
                  value="private"
                  checked={formData.permissions === 'private'}
                  onChange={handleInputChange}
                  className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300"
                />
                <label htmlFor="private" className="ml-3 flex items-center">
                  <LockClosedIcon className="h-5 w-5 text-gray-400 mr-2" />
                  <div>
                    <div className="text-sm font-medium text-gray-700">Private</div>
                    <div className="text-xs text-gray-500">Only you can access this document</div>
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/documents')}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading || files.length === 0}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? (
                <>
                  <div className="animate-spin -ml-1 mr-3 h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <CloudArrowUpIcon className="-ml-1 mr-2 h-4 w-4" />
                  Upload Documents
                </>
              )}
            </button>
          </div>
        </form>
      </div>
      
      <Footer />
    </div>
  );
};

export default DocumentUpload;
